import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";

import { savedSignIn, saveSignIn } from "@/app/savedCredentials";
import { setRemember as setRememberSession } from "@/api/tokenStore";
import { ApiError } from "@/api/errors";
import { login } from "@/api/session";
import { AccountCard, AccountFrame, FormAlert } from "@/app/AccountFrame";
import { LoginAside } from "@/app/accountPanels";
import { VersionFootnote } from "@/app/AppVersion";
import { useTrialDays } from "@/app/trial";
import { Button, Field, Input, Label } from "@/components/ui";
import { cn } from "@/lib/cn";
import { HOME_PATH } from "@/app/registry";
import { isInactiveDetail, type InactiveDetail } from "@/pages/payment/lib";
import { ActivatePayment } from "@/pages/payment/parts";
import { ResendLink } from "@/pages/password/VerifyEmailPage";

const LINK = "font-medium text-brand underline-offset-4 hover:underline";

/** 403 from /auth/login before the address is confirmed (V034). */
interface UnconfirmedDetail {
  code: "email_unconfirmed";
  message: string;
  email: string | null;
}

function isUnconfirmed(d: unknown): d is UnconfirmedDetail {
  return !!d && typeof d === "object" && (d as { code?: unknown }).code === "email_unconfirmed";
}

function message(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Wrong email or password.";
    if (error.status === 429) return "Too many attempts. Wait a few minutes and try again.";
    if (error.status === 503) return "Sign-in isn't configured on the server (AUTH_SECRET).";
    if (error.status === 0) return "Can't reach the server. Is the API running?";
    return error.message;
  }
  return "Something went wrong. Try again.";
}

export function LoginPage() {
  const search = useSearch({ strict: false }) as { redirect?: string };
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  useEffect(() => {
    let live = true;
    void savedSignIn().then((c) => {
      if (live && c) {
        setEmail(c.email);
        setPassword(c.password);
      }
    });
    return () => {
      live = false;
    };
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // the account can't sign in until its subscription is active (paid plans)
  const [inactive, setInactive] = useState<InactiveDetail | null>(null);
  // the email address isn't confirmed yet: sign-in waits for the emailed link
  const [unconfirmed, setUnconfirmed] = useState<UnconfirmedDetail | null>(null);
  const trialDays = useTrialDays();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInactive(null);
    setUnconfirmed(null);
    try {
      setRememberSession(remember); // before login stores the refresh token
      await login(email, password);
      void saveSignIn(email, password); // Chrome: "Save password?"
      await navigate({ to: search.redirect ?? HOME_PATH, replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && isInactiveDetail(err.detail)) {
        setInactive(err.detail);
      } else if (err instanceof ApiError && err.status === 403 && isUnconfirmed(err.detail)) {
        setUnconfirmed(err.detail);
      } else {
        setError(message(err));
      }
      setBusy(false);
    }
  }

  if (unconfirmed) {
    return (
      <AccountFrame title="Sign in">
        <div className="mx-auto w-full max-w-[520px]">
          <AccountCard title="Confirm your email first">
            <p role="alert" className="rounded-lg bg-warn-soft px-3 py-2 text-sm [overflow-wrap:anywhere] text-fg">
              {unconfirmed.message} Click the button in that email, then sign in again.
            </p>
            <p className="text-sm text-muted">
              Can't find it? Check your spam folder, or send a new link. You can also{" "}
              <Link to="/verify-email" search={{ email } as never} className={LINK}>
                enter the code
              </Link>{" "}
              from the email.
            </p>
            <ResendLink initialEmail={email} />
            <Button variant="ghost" size="lg" onClick={() => setUnconfirmed(null)} className="w-full sm:w-fit">
              Back to sign in
            </Button>
          </AccountCard>
        </div>
      </AccountFrame>
    );
  }

  if (inactive) {
    return (
      <AccountFrame title="Sign in">
        <AccountCard
          title={
            inactive.reason === "trial_ended"
              ? "Your free trial has ended"
              : inactive.reason === "expired"
                ? "Renew your subscription"
                : "Activate your account"
          }
        >
          <p role="alert" className="rounded-lg bg-warn-soft px-3 py-2 text-sm text-fg">
            {inactive.message}
          </p>
          <ActivatePayment detail={inactive} email={email} />
          <Button variant="secondary" size="lg" onClick={() => setInactive(null)} className="w-full sm:w-fit">
            Back to sign in
          </Button>
        </AccountCard>
      </AccountFrame>
    );
  }

  return (
    <AccountFrame title="Sign in" aside={<LoginAside />}>
      <AccountCard
        title="Sign in"
        intro={
          <>
            New here?{" "}
            <Link to="/signup" className={LINK}>
              {trialDays > 0 ? `Start your ${trialDays}-day free trial` : "Create an account"}
            </Link>
          </>
        }
      >
        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <Field label="Email" htmlFor="email">
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              required
              className="h-11"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!error || undefined}
            />
          </Field>
          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <Label htmlFor="password">Password</Label>
              <Link to="/set-password" className={cn(LINK, "text-xs")}>
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              className="h-11"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!error || undefined}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              name="remember"
              className="size-4 accent-[var(--color-brand)]"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Keep me signed in
          </label>
          {error ? <FormAlert>{error}</FormAlert> : null}
          <Button type="submit" size="lg" loading={busy} disabled={!email || !password} className="w-full">
            Sign in
          </Button>
        </form>
        <p className="mt-4 text-center">
          <VersionFootnote />
        </p>
      </AccountCard>
    </AccountFrame>
  );
}
